import { Form, Head, Link } from "@inertiajs/react"
import { ArrowLeft, Download, FileText, Loader2, Upload } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CsvUploadField } from "@/components/candidates/csv-upload-field"
import { ImportResultPanel } from "@/components/candidates/import-result-panel"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import {
  candidatesPath,
  importCandidatesPath,
  importTemplateCandidatesPath,
} from "@/routes"
import type { CandidatesImportNewProps } from "@/types/candidates"

/**
 * pages/Brand/Candidates/Imports/New — bulk CSV import surface.
 *
 * Three states swapped inline (NO route changes — UX Spec §Bulk CSV
 * page binding):
 *
 *   1. **Pre-upload** — info card with the rules, prominent
 *      "Download CSV template" link, file picker, "single-add"
 *      escape hatch. This is the empty state for the page; the
 *      <CsvUploadField> handles the picker + 500-row pre-flight.
 *   2. **Processing** — Inertia <Form> render-prop `processing` is
 *      truthy. We swap the picker for a "Validating + importing"
 *      message + indeterminate spinner. Cancel is intentionally
 *      omitted (UX Spec: don't lie about what we don't support).
 *   3. **Result** — `result` prop is non-null after a POST. The
 *      <ImportResultPanel> renders the summary + per-row failure
 *      table + copy/download CTAs. From here the user has two
 *      doors: "Upload another" (resets to pre-upload via local
 *      state — no Inertia round-trip) and "View candidates"
 *      (Link → candidatesPath).
 *
 * **Whole-file errors** (over-cap server-side / malformed CSV /
 * header mismatch / missing file) come back as `errors.file` from
 * the render-prop. We surface them inside the upload field's Alert
 * region so SR users land in the same place client-side and
 * server-side rejections show.
 *
 * **Mobile hint** — Spec §Responsive: bulk upload is desktop-first.
 * The `< sm` viewport gets a one-line Alert explaining that bulk
 * works best on desktop, but the form remains functional.
 *
 * **Locked QA testid contract** (FE TRD §testid):
 *   - `csv-template-link`, `csv-upload-field` (already on the field)
 *   - `import-submit` (the Form's submit button)
 *   - everything in the result branch (`import-result`, etc.) is on
 *     the <ImportResultPanel> component.
 */
export default function CandidatesImportNew({
  maxRows,
  result,
  // `columns` is unused in v1 — backend ships it for the future "columns
  // we'll expect" bullet list. Pre-upload rules already mention required
  // vs optional buckets verbatim from PRD Q1; the per-column list is
  // discoverable from the downloadable template.
}: CandidatesImportNewProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  const online = useOnline()
  const brandName = currentBrand?.name ?? ""

  // `viewKey` lets the user reset the result branch back to pre-upload
  // without an Inertia round-trip. We don't reload the page — just
  // remount the upload field by keying it. The `result` prop is still
  // truthy in the Inertia store, but the local override drives the
  // visible branch.
  const [resetCounter, setResetCounter] = useState(0)
  const showResult = result !== null && resetCounter === 0

  // File the user has currently picked. Drives whether the submit
  // button is interactive in the pre-upload state. The Inertia <Form>
  // submission still ships from the underlying <input type="file">
  // (CsvUploadField holds the canonical state), so this is a UI-side
  // mirror only.
  const [pickedFile, setPickedFile] = useState<File | null>(null)

  function handleUploadAnother() {
    // Single click handler: reset the local pre-upload mirror AND
    // bump the key so the underlying CsvUploadField remounts with a
    // fresh `<input type="file">` (resets the browser-held file
    // selection so the picker reads "Choose CSV file" not the
    // previous filename).
    setPickedFile(null)
    setResetCounter((n) => n + 1)
  }

  return (
    <AppLayout>
      <Head title={t("candidates.bulk.head_title")} />
      <div className="flex flex-col gap-6 p-6">
        <header className="flex flex-col gap-3">
          <Link
            href={candidatesPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="bulk-back-to-candidates"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("candidates.bulk.back_to_candidates")}
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-foreground text-2xl font-semibold tracking-tight">
              {t("candidates.bulk.title")}
            </h1>
            {brandName && (
              <Badge variant="secondary" data-testid="bulk-brand-chip">
                {brandName}
              </Badge>
            )}
          </div>
          {brandName && (
            <p className="text-muted-foreground text-sm">
              {t("candidates.bulk.brand_context", { brand: brandName })}
            </p>
          )}
        </header>

        {/* Mobile hint — desktop-first per UX Spec §Responsive. */}
        <Alert
          className="sm:hidden"
          role="status"
          data-testid="bulk-mobile-hint"
        >
          <AlertDescription>
            {t("candidates.bulk.errors.mobile_hint")}
          </AlertDescription>
        </Alert>

        {/* Offline state — Spec §States.Bulk CSV.Offline. */}
        {!online && (
          <Alert
            variant="destructive"
            role="alert"
            data-testid="bulk-offline-banner"
          >
            <AlertTitle>{t("candidates.bulk.errors.offline.title")}</AlertTitle>
            <AlertDescription>
              {t("candidates.bulk.errors.offline.body")}
            </AlertDescription>
          </Alert>
        )}

        {showResult && result ? (
          <ImportResultPanel
            result={result}
            brandName={brandName}
            actions={
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleUploadAnother}
                  data-testid="bulk-upload-another"
                  className="max-sm:h-11"
                >
                  <Upload className="size-4" aria-hidden="true" />
                  {t("candidates.bulk.result.upload_another")}
                </Button>
                <Button
                  asChild
                  data-testid="bulk-view-candidates"
                  className="max-sm:h-11"
                >
                  <Link href={candidatesPath()}>
                    {t("candidates.bulk.result.view_candidates")}
                  </Link>
                </Button>
              </>
            }
          />
        ) : (
          <PreUploadCard
            key={resetCounter}
            maxRows={maxRows}
            online={online}
            pickedFile={pickedFile}
            onFileChange={setPickedFile}
          />
        )}
      </div>
    </AppLayout>
  )
}

interface PreUploadCardProps {
  maxRows: number
  online: boolean
  pickedFile: File | null
  onFileChange: (file: File | null) => void
}

function PreUploadCard({
  maxRows,
  online,
  pickedFile,
  onFileChange,
}: PreUploadCardProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  const brandName = currentBrand?.name ?? ""

  return (
    <section className="flex flex-col gap-6">
      <RulesCard maxRows={maxRows} brandName={brandName} />

      <Form
        action={importCandidatesPath()}
        method="post"
        options={{ preserveScroll: true }}
        className="flex flex-col gap-4"
      >
        {({ processing, errors }) => {
          if (processing) {
            return <ProcessingState />
          }
          return (
            <>
              <CsvUploadField
                maxRows={maxRows}
                disabled={!online}
                onFileChange={onFileChange}
                serverError={errors.file as string | string[] | undefined}
              />
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="submit"
                  disabled={!online || pickedFile === null || processing}
                  aria-disabled={
                    !online || pickedFile === null || processing
                      ? "true"
                      : undefined
                  }
                  data-testid="import-submit"
                  className="max-sm:h-11"
                >
                  <Upload className="size-4" aria-hidden="true" />
                  {t("candidates.bulk.pre_upload.submit")}
                </Button>
              </div>
            </>
          )
        }}
      </Form>

      <p className="text-muted-foreground text-sm">
        <Link
          href={candidatesPath()}
          className="text-foreground underline-offset-2 hover:underline"
          data-testid="bulk-single-cta"
        >
          {t("candidates.bulk.pre_upload.single_cta")}
        </Link>
      </p>
    </section>
  )
}

interface RulesCardProps {
  maxRows: number
  brandName: string
}

function RulesCard({ maxRows, brandName }: RulesCardProps) {
  const { t } = useTranslation()
  return (
    <div className="bg-muted/30 flex flex-col gap-3 rounded-md border p-4">
      <h2 className="text-foreground inline-flex items-center gap-2 text-sm font-semibold">
        <FileText className="size-4" aria-hidden="true" />
        {t("candidates.bulk.pre_upload.rules_heading")}
      </h2>
      <ul className="text-muted-foreground flex flex-col gap-1.5 text-sm">
        <li>{t("candidates.bulk.pre_upload.rule_required")}</li>
        <li>{t("candidates.bulk.pre_upload.rule_optional")}</li>
        <li>
          {t("candidates.bulk.pre_upload.rule_cap").replace(
            "500",
            String(maxRows),
          )}
        </li>
        <li>{t("candidates.bulk.pre_upload.rule_tags")}</li>
        {brandName && (
          <li>
            {t("candidates.bulk.pre_upload.rule_brand", { brand: brandName })}
          </li>
        )}
      </ul>
      <Button asChild variant="outline" size="sm" className="w-fit max-sm:h-11">
        <a
          href={importTemplateCandidatesPath({ format: "csv" })}
          download
          data-testid="csv-template-link"
        >
          <Download className="size-4" aria-hidden="true" />
          {t("candidates.bulk.pre_upload.template_link")}
        </a>
      </Button>
      {/* OKS-129 — the template ships one worked example row; tell the
          user to replace it. Guidance is text-only (no colour signal). */}
      <p
        className="text-muted-foreground text-sm"
        data-testid="template-example-note"
      >
        {t("candidates.bulk.pre_upload.template_example_note")}
      </p>
    </div>
  )
}

function ProcessingState() {
  const { t } = useTranslation()
  return (
    <div
      className="border-primary/30 bg-primary/5 flex items-center gap-3 rounded-md border p-4"
      role="status"
      aria-live="polite"
      data-testid="import-processing"
    >
      <Loader2
        className="text-primary size-5 animate-spin"
        aria-hidden="true"
      />
      <div className="flex flex-col">
        <span className="text-foreground text-sm font-medium">
          {t("candidates.bulk.processing.title")}
        </span>
        <span className="text-muted-foreground text-sm">
          {t("candidates.bulk.processing.message_indeterminate")}
        </span>
      </div>
    </div>
  )
}
